// src/lib/utils/image.ts

/**
 * Compress an image File to a new File of the same type,
 * using data-URI + <canvas> instead of blob URLs.
 */
interface CompressOptions {
  maxWidth: number;
  maxHeight: number;
  quality: number;
}

export async function compressImage(
  file: File,
  options: CompressOptions = {
    maxWidth: 2048,
    maxHeight: 2048,
    quality: 0.8,
  }
): Promise<File> {
  // 1) Read file as data-URL (allowed by CSP)
  const dataUrl: string = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.onload = () => resolve(reader.result as string);
    reader.readAsDataURL(file);
  });

  // 2) Load that data-URL into an Image
  const img: HTMLImageElement = await new Promise((resolve, reject) => {
    const img = new Image();
    img.onerror = () => reject(new Error('Failed to load image'));
    img.onload = () => resolve(img);
    img.src = dataUrl;          // ← NO createObjectURL here
  });

  // 3) Resize logic
  let { width, height } = img;
  if (width > options.maxWidth) {
    height = Math.round((height * options.maxWidth) / width);
    width = options.maxWidth;
  }
  if (height > options.maxHeight) {
    width = Math.round((width * options.maxHeight) / height);
    height = options.maxHeight;
  }

  // 4) Draw + compress on canvas
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Failed to get canvas context');
  ctx.drawImage(img, 0, 0, width, height);

  // 5) Export as Blob + wrap as File
  const blob: Blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      b => (b ? resolve(b) : reject(new Error('Canvas is empty'))),
      file.type,
      options.quality
    );
  });

  return new File([blob], file.name, {
    type: file.type,
    lastModified: Date.now(),
  });
}
